/**
 *
 * @description
 * A predicate is a function that returns a boolean.
 */
export type Predicate<T> = (v: T) => boolean;

// -- combinators

/**
 *
 * @description
 * Derives a new predicate that is the negation of the given predicate.
 * Truth table:
 * | A | ¬A |
 * |---|----|
 * | 0 |  1 |
 * | 1 |  0 |
 */
export const not = <T>(pred: Predicate<T>): Predicate<T> => {
  return (v) => !pred(v);
};

/**
 *
 * @description
 * Derives a new predicate that is the conjunction of the given predicates.
 * Truth table:
 * | A | B | A ∧ B |
 * |---|---|-------|
 * | 0 | 0 |     0 |
 * | 0 | 1 |     0 |
 * | 1 | 0 |     0 |
 * | 1 | 1 |     1 |
 */
export const and = <T>(...preds: Predicate<T>[]): Predicate<T> => {
  return (v) => preds.every((pred) => pred(v));
};

/**
 *
 * @description
 * Derives a new predicate that is the disjunction of the given predicates.
 * Truth table:
 * | A | B | A ∨ B |
 * |---|---|-------|
 * | 0 | 0 |     0 |
 * | 0 | 1 |     1 |
 * | 1 | 0 |     1 |
 * | 1 | 1 |     1 |
 */
export const or = <T>(...preds: Predicate<T>[]): Predicate<T> => {
  return (v) => preds.some((pred) => pred(v));
};

/**
 *
 * @description
 * Derives a new predicate that is the non-disjunction of the given predicates.
 * Truth table:
 * | A | B | A ↓ B |
 * |---|---|-------|
 * | 0 | 0 |     1 |
 * | 0 | 1 |     0 |
 * | 1 | 0 |     0 |
 * | 1 | 1 |     0 |
 */
export const nor = <T>(...preds: Predicate<T>[]): Predicate<T> => {
  return not(or(...preds));
};

/**
 *
 * @description
 * Derives a new predicate that is the exclusive disjunction of the given predicates.
 * Truth table:
 * | A | B | A ⊕ B |
 * |---|---|-------|
 * | 0 | 0 |     0 |
 * | 0 | 1 |     1 |
 * | 1 | 0 |     1 |
 * | 1 | 1 |     0 |
 */
export const xor = <T>(...preds: Predicate<T>[]): Predicate<T> => {
  return (v) => preds.filter((pred) => pred(v)).length === 1;
};

/**
 *
 * @description
 * Derives a new predicate that is the non-conjonction of the given predicates.
 * Truth table:
 * | A | B | A . B |
 * |---|---|-------|
 * | 0 | 0 |     1 |
 * | 0 | 1 |     1 |
 * | 1 | 0 |     1 |
 * | 1 | 1 |     0 |
 */
export const nand = <T>(...preds: Predicate<T>[]): Predicate<T> => {
  return not(and(...preds));
};

/**
 *
 * @description
 * Derives a new predicate that is the non-exclusive disjunction of the given predicates.
 * Truth table:
 * | A | B | A ⊙ B |
 * |---|---|-------|
 * | 0 | 0 |     1 |
 * | 0 | 1 |     0 |
 * | 1 | 0 |     0 |
 * | 1 | 1 |     1 |
 */
export const xnor = <T>(...preds: Predicate<T>[]): Predicate<T> => {
  return (v) => {
    const count = preds.filter((pred) => pred(v)).length;
    return count === 0 || count === preds.length;
  };
};

/**
 *
 * @description
 * Alias for xnor.
 */
export const xand = xnor;

/**
 *
 * @description
 * Derives a new predicate on a record that is the conjunction of the given predicates on the values.
 */
export const andRecord = <T>(
  pred: Predicate<T>
): Predicate<Record<string, T>> => {
  return (v) => Object.values(v).every(pred);
};

/**
 *
 * @description
 * Derives a new predicate on a record that is the disjunction of the given predicates on the values.
 */
export const orRecord = <T>(
  pred: Predicate<T>
): Predicate<Record<string, T>> => {
  return (v) => Object.values(v).some(pred);
};

// -- mappers

/**
 *
 * @description
 * Derives a predicate on type U from a predicate on type T.
 */
export const contramap = <T, U>(
  pred: Predicate<T>,
  fn: (v: U) => T
): Predicate<U> => {
  return (v) => pred(fn(v));
};

// -- Predicates on numbers

/**
 *
 * @description
 * Returns true if the number is positive.
 */
export const isPositive = (n: number) => n > 0;

/**
 *
 * @description
 * Returns true if the number is negative.
 */
export const isNegative = (n: number) => n < 0;

/**
 *
 * @description
 * Returns true if the number is zero.
 */
export const isZero = nor(isPositive, isNegative);

/**
 *
 * @description
 * Returns true if the number is non-positive.
 */
export const isNonPositive = not(isPositive);

/**
 *
 * @description
 * Returns true if the number is non-negative.
 */
export const isNonNegative = not(isNegative);

/**
 *
 * @description
 * Returns true if the number is even.
 */
export const isEven = (n: number) => n % 2 === 0;

/**
 *
 * @description
 * Returns true if the number is odd.
 */
export const isOdd = not(isEven);

// -- Predicates on strings

/**
 *
 * @description
 * Returns true if the string is empty.
 */
export const isEmpty = (s: string) => s.length === 0;

/**
 *
 * @description
 * Returns true if the string is not empty.
 */
export const isNotEmpty = not(isEmpty);

/**
 *
 * @description
 * Returns true if the string is all uppercase.
 */
export const isUpperCase = (s: string) => s === s.toUpperCase();

/**
 *
 * @description
 * Returns true if the string is all lowercase.
 */
export const isLowerCase = (s: string) => s === s.toLowerCase();
